import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useLogin, useNotify, useTranslate } from "ra-core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { SignUpData } from "../types";
import { LoginSkeleton } from "./LoginSkeleton";
import { Notification } from "@/components/admin/notification";
import { ConfirmationRequired } from "./ConfirmationRequired";
import { SSOAuthButton } from "./SSOAuthButton";
import { googleWorkplaceDomain } from "./authConfig";
import { isOfficialEmail } from "@/lib/officialEmail";

export const SignupPage = () => {
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { darkModeLogo: logo, title } = useConfigurationContext();
  const navigate = useNavigate();
  const translate = useTranslate();
  const { data: isInitialized, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => {
      return dataProvider.isInitialized();
    },
  });

  const { isPending: isSignUpPending, mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SignUpData) => {
      return dataProvider.signUp(data);
    },
    onSuccess: (data) => {
      login({
        email: data.email,
        password: data.password,
        redirectTo: "/contacts",
      })
        .then(() => {
          notify("crm.auth.signup.initial_user_created", {
            messageArgs: {
              _: "Initial user successfully created",
            },
          });
          // FIXME: We should probably provide a hook for that in the ra-core package
          queryClient.invalidateQueries({
            queryKey: ["auth", "canAccess"],
          });
        })
        .catch((err) => {
          if (err.code === "email_not_confirmed") {
            // An email confirmation is required to continue.
            navigate(ConfirmationRequired.path);
          } else {
            notify("crm.auth.sign_in_failed", {
              type: "error",
              messageArgs: {
                _: "Failed to log in.",
              },
            });
            navigate("/login");
          }
        });
    },
    onError: (error) => {
      notify(error.message);
    },
  });

  const login = useLogin();
  const notify = useNotify();

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<SignUpData>({
    mode: "onChange",
  });

  if (isPending) {
    return <LoginSkeleton />;
  }

  // For the moment, we only allow one user to sign up. Other users must be created by the administrator.
  if (isInitialized) {
    return <Navigate to="/login" />;
  }

  const onSubmit: SubmitHandler<SignUpData> = async (data) => {
    mutate(data);
  };

  return (
    <div className="h-screen p-8">
      <div className="flex items-center gap-4">
        <img src={logo} alt={title} width={24} className="h-8 w-auto" />
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>
      <div className="h-full">
        <div className="max-w-sm mx-auto h-full flex flex-col justify-center gap-4">
          <h1 className="text-2xl font-bold mb-4">
            {translate("crm.auth.welcome_title", {
              _: "Welcome to Atomic CRM",
            })}
          </h1>
          <p className="text-base mb-4">
            {translate("crm.auth.signup.create_first_user", {
              _: "Create the first user account to complete the setup.",
            })}
          </p>
          {/* design-system.md section 4/6: borderless white card on the paper
              background, no other frame around the form. */}
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="first_name">
                    {translate("crm.auth.first_name")}
                  </Label>
                  <Input
                    {...register("first_name", { required: true })}
                    id="first_name"
                    type="text"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="last_name">
                    {translate("crm.auth.last_name")}
                  </Label>
                  <Input
                    {...register("last_name", { required: true })}
                    id="last_name"
                    type="text"
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">{translate("ra.auth.email")}</Label>
                  <Input
                    {...register("email", {
                      required: true,
                      validate: (value) =>
                        isOfficialEmail(value) ||
                        translate("crm.auth.official_email_required"),
                    })}
                    id="email"
                    type="email"
                    required
                    aria-invalid={errors.email ? true : undefined}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email?.message ? (
                    <p
                      id="email-error"
                      role="alert"
                      className="text-sm text-destructive"
                    >
                      {errors.email.message}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">
                    {translate("ra.auth.password")}
                  </Label>
                  <Input
                    {...register("password", { required: true })}
                    id="password"
                    type="password"
                    required
                  />
                </div>
                <div className="flex flex-col gap-4 justify-between items-center mt-8">
                  <Button
                    type="submit"
                    variant="gradient"
                    disabled={!isValid || isSignUpPending}
                    className="w-full"
                  >
                    {isSignUpPending ? (
                      <>
                        <Spinner
                          className="text-primary-foreground size-4"
                          data-icon="inline-start"
                        />
                        {translate("crm.auth.signup.creating", {
                          _: "Creating...",
                        })}
                      </>
                    ) : (
                      translate("crm.auth.signup.create_account", {
                        _: "Create account",
                      })
                    )}
                  </Button>
                  {googleWorkplaceDomain ? (
                    <SSOAuthButton
                      className="w-full"
                      domain={googleWorkplaceDomain}
                    >
                      {translate("crm.auth.sign_in_google_workspace", {
                        _: "Sign in with Google Workplace",
                      })}
                    </SSOAuthButton>
                  ) : null}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <Notification />
    </div>
  );
};

SignupPage.path = "/sign-up";
